/* eslint-disable max-len */
import AdjustDeductible from '../components/AdjustCoverage/AdjustDeductible';

import type { ElementType } from 'react';
import type { AdjustCoverageAction, AdjustCoverageInformationProps } from '../types';
import type {
    EditTermPriceDTO,
    OPCTermDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc';
import { type DSSPaymentFrequency } from 'gw-edge-client/dist/types/dto/pc/typekey';
import { getCurrencyString, paymentFrequencyMap } from '../../../utils/currencyUtil';
import AdjustInjuryDegree from '../components/AdjustCoverage/AdjustInjuryDegree';
import AdjustInsuranceSum from '../components/AdjustCoverage/AdjustInsuranceSum';
import AdjustAudioTelco from '../components/AdjustCoverage/AdjustAudioTelco';
import { formatMonetaryAmountToStringWithoutCurrency } from '../../../utils/coverablesUtil';
import AdjustPrhHolidayInsuranceSum from '../components/AdjustCoverage/AdjustPrhHolidayInsuranceSum';
import type { AmountDTO } from 'gw-edge-client/dist/types/dto/pc/edge/capabilities/currency/dto';
import AdjustUvpContentSum from '../components/AdjustCoverage/AdjustUvpContentSum';
import type { PricesDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/payment';

interface AdjustCoverage {
    title: string;
    AdjustComponent: ElementType<{
        termInfo: OPCTermDTO;
        updateTermValue: (termValue: number) => void;
        updateTermStringValue: (termValue: string) => void;
        isAdjustableDataValid: boolean;
    }>;
    adjustInformationProps?: AdjustCoverageInformationProps;
}

const getFormattedTerms = (
    originalTermValue: number | string,
    newTermValue: AmountDTO | undefined,
    unitValue: string
): { oldTermDisplay: string; newTermDisplay: string } => {
    if (typeof originalTermValue === 'number') {
        // Default behavior
        const oldTermDisplay = `${formatMonetaryAmountToStringWithoutCurrency(originalTermValue)} ${unitValue}`;
        const newTermDisplay =
            newTermValue !== undefined
                ? `${formatMonetaryAmountToStringWithoutCurrency(newTermValue?.amount ?? 0)} ${unitValue}`
                : '-';
        return {
            oldTermDisplay,
            newTermDisplay
        };
    } else if (typeof originalTermValue === 'string') {
        // currently only support OPC - PRH Holiday Home
        const oldTermDisplay = originalTermValue;
        const newTermDisplay = newTermValue !== undefined ? newTermValue?.amountString ?? '0' : '-';
        return {
            oldTermDisplay,
            newTermDisplay
        };
    }
    throw new Error('Add implementation for your lob');
};

const getCommonAdjustProps = (
    originalTermValue: number | string,
    editTermPriceDTO: EditTermPriceDTO,
    unitValue: string,
    termName: string,
    originalOldPrice: PricesDTO | undefined,
    selectedPaymentFrequency?: DSSPaymentFrequency
): AdjustCoverageInformationProps => {
    const oldPriceLabel = 'DSS-COVERABLE-DETAILS-PRICE-AFFECT-BEFORE';
    const newPriceLabel = 'DSS-COVERABLE-DETAILS-PRICE-AFFECT-AFTER';

    const title = undefined;
    const { newPrice, oldPrice: editTermOldPrice, newTermValue } = editTermPriceDTO;
    const oldPrice = originalOldPrice ?? editTermOldPrice;
    const isMonthly = selectedPaymentFrequency === paymentFrequencyMap.monthly;
    const oldPriceAmount = isMonthly ? oldPrice?.monthlyTotal.amount : oldPrice?.yearlyTotal.amount;
    const newPriceAmount = isMonthly ? newPrice?.monthlyTotal.amount : newPrice?.yearlyTotal.amount;
    const oldPriceDisplay = getCurrencyString(oldPriceAmount ?? 0, ' kr.', 2);
    const newPriceDisplay =
        newPriceAmount !== undefined ? getCurrencyString(newPriceAmount, ' kr.', 2) : '-';
    const { oldTermDisplay, newTermDisplay } = getFormattedTerms(
        originalTermValue,
        newTermValue,
        unitValue
    );
    const mobileProps = {
        before: {
            price: {
                label: oldPriceLabel,
                value: oldPriceDisplay
            },
            attribute: {
                label: termName,
                value: oldTermDisplay
            }
        },
        after: {
            price: {
                label: newPriceLabel,
                value: newPriceDisplay
            },
            attribute: {
                label: termName,
                value: newTermDisplay
            }
        }
    };

    const desktopProps = {
        before: {
            priceLable: oldPriceLabel,
            priceValue: `${isMonthly ? 'Månedlig' : 'Årlig'} pris: ${oldPriceDisplay}`,
            attributeValue: `${termName}: ${oldTermDisplay}`
        },
        after: {
            priceLable: newPriceLabel,
            priceValue: `${isMonthly ? 'Månedlig' : 'Årlig'} pris: ${newPriceDisplay}`,
            attributeValue: `${termName}: ${newTermDisplay}`
        }
    };

    const detailPriceText = 'DSS-OPC-PRICE-TABLE-SUBTITLE';
    return { title, mobileProps, desktopProps, detailPriceText };
};

const getAdjustDeductibleProps = (
    originalTermValue: number | string,
    editTermPriceDTO: EditTermPriceDTO,
    originalPrice: PricesDTO | undefined,
    selectedPaymentFrequency?: DSSPaymentFrequency
): AdjustCoverageInformationProps => {
    return getCommonAdjustProps(
        originalTermValue,
        editTermPriceDTO,
        'kr.',
        'Selvrisiko',
        originalPrice,
        selectedPaymentFrequency
    );
};

const getAdjustInjuryDegreeProps = (
    originalTermValue: number,
    editTermPriceDTO: EditTermPriceDTO,
    originalPrice: PricesDTO | undefined,
    selectedPaymentFrequency?: DSSPaymentFrequency
): AdjustCoverageInformationProps => {
    return getCommonAdjustProps(
        originalTermValue,
        editTermPriceDTO,
        '%',
        'Méngrad',
        originalPrice,
        selectedPaymentFrequency
    );
};

const getAdjustSumInsuredProps = (
    originalTermValue: number | string,
    editTermPriceDTO: EditTermPriceDTO,
    originalPrice: PricesDTO | undefined,
    selectedPaymentFrequency?: DSSPaymentFrequency
): AdjustCoverageInformationProps => {
    return getCommonAdjustProps(
        originalTermValue,
        editTermPriceDTO,
        'kr.',
        'Forsikringssum',
        originalPrice,
        selectedPaymentFrequency
    );
};

const getAdjustCoverageConfig = (
    action: AdjustCoverageAction,
    editTermPriceDTO: EditTermPriceDTO,
    originalTermValue: number,
    originalTermStringValue: string,
    originalPrice: PricesDTO | undefined,
    selectedPaymentFrequency?: DSSPaymentFrequency
): AdjustCoverage => {
    switch (action) {
        case 'Deductible':
            return {
                title: 'DSS-COVERABLE-DETAILS-ADAPT-DEDUCTIBLE',
                AdjustComponent: AdjustDeductible,
                adjustInformationProps: getAdjustDeductibleProps(
                    originalTermValue,
                    editTermPriceDTO,
                    originalPrice,
                    selectedPaymentFrequency
                )
            };
        case 'AudioTelcoDropdown':
            return {
                title: 'DSS-COVERABLE-DETAILS-ADAPT-SUM-HEADER',
                AdjustComponent: AdjustAudioTelco,
                adjustInformationProps: getCommonAdjustProps(
                    originalTermValue,
                    editTermPriceDTO,
                    'kr.',
                    'Sum',
                    originalPrice,
                    selectedPaymentFrequency
                )
            };
        case 'InjuryDegree':
            return {
                title: 'DSS-COVERABLE-DETAILS-INJURY-DEGREE-HEADER',
                AdjustComponent: AdjustInjuryDegree,
                adjustInformationProps: getAdjustInjuryDegreeProps(
                    originalTermValue,
                    editTermPriceDTO,
                    originalPrice,
                    selectedPaymentFrequency
                )
            };
        case 'Limit':
            return {
                title: 'DSS-COVERABLE-DETAILS-ADAPT-SUM-HEADER',
                AdjustComponent: AdjustInsuranceSum,
                adjustInformationProps: getAdjustSumInsuredProps(
                    originalTermValue,
                    editTermPriceDTO,
                    originalPrice,
                    selectedPaymentFrequency
                )
            };
        case 'PRHPrivateContentSum':
            return {
                title: 'DSS-COVERABLE-DETAILS-ADAPT-SUM-HEADER',
                AdjustComponent: AdjustPrhHolidayInsuranceSum,
                adjustInformationProps: getAdjustSumInsuredProps(
                    originalTermStringValue,
                    editTermPriceDTO,
                    originalPrice,
                    selectedPaymentFrequency
                )
            };
        case 'UVPContentSum':
            return {
                title: 'DSS-COVERABLE-DETAILS-ADAPT-SUM-HEADER',
                AdjustComponent: AdjustUvpContentSum,
                adjustInformationProps: getAdjustSumInsuredProps(
                    originalTermValue,
                    editTermPriceDTO,
                    originalPrice,
                    selectedPaymentFrequency
                )
            };
        default:
            throw new Error(`Unsupported for action: ${String(action)}`);
    }
};

export default getAdjustCoverageConfig;
